"use client"

import * as React from "react"
import { cn } from "../lib/utils"
import type { ChoiceInteraction } from "../schema"
import type {
	DisplayBlock,
	DisplayChoiceInteraction,
	DisplayGapMatchInteraction,
	DisplayItem,
	DisplayMatchInteraction,
	DisplayOrderInteraction
} from "../shared/types"
import { FeedbackMessage } from "./feedback-message"
import { ChoiceInteractionRenderer } from "./interactions/choice-interaction"
import { GapMatchInteraction } from "./interactions/gap-match-interaction"
import { InlineInteraction } from "./interactions/inline-interaction"
import { MatchInteraction } from "./interactions/match-interaction"
import { OrderInteraction } from "./interactions/order-interaction"
import { InlineTextEntry, TextEntryInteraction } from "./interactions/text-entry-interaction"
import { HTMLContent } from "./shared/html-content"

// ============================================================================
// Order Interaction Block (with auto-initialization)
// ============================================================================

/**
 * Wrapper for OrderInteraction that initializes the response with the displayed
 * order on mount. This ensures that if the user submits without interacting,
 * the current visible order is submitted (not an empty response).
 */
function OrderInteractionBlock({
	interaction,
	responses,
	onAnswerSelect,
	disabled,
	showFeedback,
	perResponseFeedback
}: {
	interaction: DisplayOrderInteraction
	responses?: Record<string, string | string[]>
	onAnswerSelect?: (responseIdentifier: string, value: string | string[]) => void
	disabled?: boolean
	showFeedback?: boolean
	perResponseFeedback?: Record<string, { isCorrect: boolean; messageHtml?: string }>
}) {
	const response = responses?.[interaction.responseId]
	const hasInitialized = React.useRef(false)

	// Initialize response with displayed order on mount (if not already set)
	React.useEffect(() => {
		// Only initialize once, and only if there's no existing response
		if (hasInitialized.current) return
		if (response) return
		if (interaction.choices.length === 0) return
		if (!onAnswerSelect) return

		hasInitialized.current = true
		const initialOrder = interaction.choices.map((c) => c.id)
		onAnswerSelect(interaction.responseId, initialOrder)
	}, [response, interaction.choices, interaction.responseId, onAnswerSelect])

	// Safe casting for array response
	let responseArray: string[] = []
	if (Array.isArray(response)) {
		responseArray = response
	} else if (typeof response === "string") {
		responseArray = [response]
	}

	const feedback = perResponseFeedback?.[interaction.responseId]

	return (
		<>
			<OrderInteraction
				interaction={interaction}
				response={responseArray}
				onAnswerSelect={(val) => onAnswerSelect?.(interaction.responseId, val)}
				disabled={disabled}
				hasSubmitted={showFeedback}
				isCorrect={feedback?.isCorrect}
			/>
			<FeedbackMessage
				responseId={interaction.responseId}
				showFeedback={showFeedback}
				perResponseFeedback={perResponseFeedback}
			/>
		</>
	)
}

// ============================================================================
// Gap Match Interaction Block
// ============================================================================

/**
 * Wrapper for GapMatchInteraction that computes per-gap correctness
 * and handles the directedPair response format.
 */
function GapMatchInteractionBlock({
	interaction,
	responses,
	onAnswerSelect,
	disabled,
	showFeedback,
	perResponseFeedback,
	selectedChoicesByResponse
}: {
	interaction: DisplayGapMatchInteraction
	responses?: Record<string, string | string[]>
	onAnswerSelect?: (responseIdentifier: string, value: string | string[]) => void
	disabled?: boolean
	showFeedback?: boolean
	perResponseFeedback?: Record<string, { isCorrect: boolean; messageHtml?: string }>
	selectedChoicesByResponse?: Record<string, Array<{ id: string; isCorrect: boolean }>>
}) {
	const response = responses?.[interaction.responseId]

	// Ensure response is array
	let responseArray: string[] = []
	if (Array.isArray(response)) {
		responseArray = response
	} else if (typeof response === "string" && response) {
		responseArray = [response]
	}

	// Compute per-gap correctness from selectedChoicesByResponse
	// Each entry in selectedChoicesByResponse has id = "sourceId gapId" format
	const gapCorrectness = React.useMemo(() => {
		const entries = selectedChoicesByResponse?.[interaction.responseId]
		if (!entries || !showFeedback) return undefined

		const result: Record<string, boolean> = {}
		for (const entry of entries) {
			// Parse "sourceId gapId" to extract gapId
			const parts = entry.id.split(" ")
			const gapId = parts[1]
			if (gapId) {
				result[gapId] = entry.isCorrect
			}
		}
		return result
	}, [selectedChoicesByResponse, interaction.responseId, showFeedback])

	return (
		<>
			<GapMatchInteraction
				interaction={interaction}
				response={responseArray}
				onAnswerSelect={(val) => onAnswerSelect?.(interaction.responseId, val)}
				disabled={disabled}
				hasSubmitted={showFeedback}
				gapCorrectness={gapCorrectness}
			/>
			<FeedbackMessage
				responseId={interaction.responseId}
				showFeedback={showFeedback}
				perResponseFeedback={perResponseFeedback}
			/>
		</>
	)
}

// ============================================================================
// Match Interaction Block
// ============================================================================

/**
 * Wrapper for MatchInteraction that computes per-pair correctness
 * and handles the directedPair response format.
 */
function MatchInteractionBlock({
	interaction,
	responses,
	onAnswerSelect,
	disabled,
	showFeedback,
	perResponseFeedback,
	selectedChoicesByResponse
}: {
	interaction: DisplayMatchInteraction
	responses?: Record<string, string | string[]>
	onAnswerSelect?: (responseIdentifier: string, value: string | string[]) => void
	disabled?: boolean
	showFeedback?: boolean
	perResponseFeedback?: Record<string, { isCorrect: boolean; messageHtml?: string }>
	selectedChoicesByResponse?: Record<string, Array<{ id: string; isCorrect: boolean }>>
}) {
	const response = responses?.[interaction.responseId]

	// Ensure response is array
	let responseArray: string[] = []
	if (Array.isArray(response)) {
		responseArray = response
	} else if (typeof response === "string" && response) {
		responseArray = [response]
	}

	// Compute per-pair correctness from selectedChoicesByResponse
	// Each entry in selectedChoicesByResponse has id = "sourceId targetId" format
	const pairCorrectness = React.useMemo(() => {
		const entries = selectedChoicesByResponse?.[interaction.responseId]
		if (!entries || !showFeedback) return undefined

		const result: Record<string, boolean> = {}
		for (const entry of entries) {
			// The id is already "sourceId targetId" format
			result[entry.id] = entry.isCorrect
		}
		return result
	}, [selectedChoicesByResponse, interaction.responseId, showFeedback])

	return (
		<>
			<MatchInteraction
				interaction={interaction}
				response={responseArray}
				onAnswerSelect={(val) => onAnswerSelect?.(interaction.responseId, val)}
				disabled={disabled}
				hasSubmitted={showFeedback}
				pairCorrectness={pairCorrectness}
			/>
			<FeedbackMessage
				responseId={interaction.responseId}
				showFeedback={showFeedback}
				perResponseFeedback={perResponseFeedback}
			/>
		</>
	)
}

// ============================================================================
// Types
// ============================================================================

/** Theme options for the QTI renderer */
export type QTITheme = "duolingo" | "neobrutalist" | (string & {})

/** Feedback result for a single response */
export interface ResponseFeedback {
	isCorrect: boolean
	messageHtml?: string
}

/** Correctness info for selected choices */
export interface ChoiceCorrectness {
	id: string
	isCorrect: boolean
}

/**
 * Props for the QTIRenderer component.
 *
 * This is a **controlled, display-only component**. It renders the question
 * and captures user input, but does NOT include submit buttons or flow logic.
 * The consuming app is responsible for:
 * - Providing responses state
 * - Handling submission
 * - Providing feedback after validation
 * - Rendering buttons (Check, Try Again, Continue, etc.)
 */
export interface QTIRendererProps {
	/**
	 * The display-safe item to render.
	 * Use `buildDisplayModel()` to create this from a parsed AssessmentItem.
	 */
	item: DisplayItem

	/**
	 * Current user responses, keyed by response identifier.
	 * This is a controlled component - you must manage this state.
	 * @example { "RESPONSE": "A" } or { "RESPONSE": ["A", "C"] }
	 */
	responses: Record<string, string | string[]>

	/**
	 * Callback fired when user selects/changes an answer.
	 * Update your responses state in this callback.
	 */
	onResponseChange: (responseId: string, value: string | string[]) => void

	/**
	 * Theme to apply. Defaults to "duolingo".
	 * Available themes: "duolingo", "neobrutalist", or any custom theme
	 * defined via `[data-qti-theme="your-theme"]` CSS.
	 */
	theme?: QTITheme

	/**
	 * Whether to show feedback states (correct/incorrect styling).
	 * Set to true after validation to display results.
	 */
	showFeedback?: boolean

	/**
	 * Whether to disable all interactions.
	 * Typically set to true when showFeedback is true.
	 */
	disabled?: boolean

	/**
	 * Per-choice correctness for highlighting selected answers.
	 * Keyed by response identifier.
	 */
	choiceCorrectness?: Record<string, ChoiceCorrectness[]>

	/**
	 * Per-response feedback messages to display.
	 * Keyed by response identifier.
	 */
	responseFeedback?: Record<string, ResponseFeedback>

	/**
	 * Overall feedback to display at the end of the question.
	 */
	overallFeedback?: ResponseFeedback

	/**
	 * Additional CSS class for the container.
	 */
	className?: string
}

export interface ContentBlockRendererProps {
	/** The content block to render */
	block: DisplayBlock
	/** Current user responses */
	responses?: Record<string, string | string[]>
	/** Callback when user selects an answer */
	onAnswerSelect?: (responseIdentifier: string, value: string | string[]) => void
	/** Whether to show feedback */
	showFeedback?: boolean
	/** Whether to disable interactions */
	disabled?: boolean
	/** Selected-only correctness mapping for this render pass */
	selectedChoicesByResponse?: Record<string, Array<{ id: string; isCorrect: boolean }>>
	/** Optional per-response feedback messages from server */
	perResponseFeedback?: Record<string, { isCorrect: boolean; messageHtml?: string }>
}

// ============================================================================
// Content Block Renderer
// ============================================================================

/**
 * Renders a single content block (stimulus or interaction)
 */
export function ContentBlockRenderer({
	block,
	responses,
	onAnswerSelect,
	showFeedback,
	disabled,
	selectedChoicesByResponse,
	perResponseFeedback
}: ContentBlockRendererProps) {
	if (block.type === "stimulus") {
		return <HTMLContent html={block.html} className="mb-6" />
	}
	if (block.type === "richStimulus") {
		// Collect all response IDs for feedback (inline choice + text entry)
		const allResponseIds = [
			...Object.values(block.inlineEmbeds).map((e) => e.responseId),
			...Object.values(block.textEmbeds).map((e) => e.responseId)
		]

		return (
			<>
				<HTMLContent
					html={block.html}
					className="mb-6"
					inlineEmbeds={block.inlineEmbeds}
					textEmbeds={block.textEmbeds}
					renderInline={(embed) => {
						const currentValue = (() => {
							const v = responses?.[embed.responseId]
							return typeof v === "string" ? v : ""
						})()
						// Compute correctness of selected value if available
						const perSelectionEntries = selectedChoicesByResponse?.[embed.responseId]
						const selectedIsCorrect =
							perSelectionEntries && currentValue
								? perSelectionEntries.find((e) => e.id === currentValue)?.isCorrect
								: undefined
						return (
							<InlineInteraction
								embed={embed}
								response={currentValue}
								onAnswerSelect={(v) => {
									if (onAnswerSelect) onAnswerSelect(embed.responseId, v)
								}}
								disabled={disabled || showFeedback}
								hasSubmitted={showFeedback}
								isCorrect={selectedIsCorrect}
							/>
						)
					}}
					renderTextEntry={(embed) => {
						const currentValue = (() => {
							const v = responses?.[embed.responseId]
							return typeof v === "string" ? v : ""
						})()
						// Text entry correctness from perResponseFeedback
						const feedback = perResponseFeedback?.[embed.responseId]
						return (
							<InlineTextEntry
								key={embed.responseId}
								embed={embed}
								response={currentValue}
								onAnswerSelect={(v) => {
									if (onAnswerSelect) onAnswerSelect(embed.responseId, v)
								}}
								disabled={disabled || showFeedback}
								hasSubmitted={showFeedback}
								isCorrect={feedback?.isCorrect}
							/>
						)
					}}
				/>
				{allResponseIds.map((responseId) => (
					<FeedbackMessage
						key={`fb-${responseId}`}
						responseId={responseId}
						showFeedback={showFeedback}
						perResponseFeedback={perResponseFeedback}
					/>
				))}
			</>
		)
	}

	if (block.type === "interaction" && block.interaction) {
		const interaction = block.interaction

		// Handle text entry interactions
		if (interaction.type === "text") {
			const response = responses?.[interaction.responseId]
			const currentValue = typeof response === "string" ? response : ""
			const feedback = perResponseFeedback?.[interaction.responseId]

			return (
				<>
					<TextEntryInteraction
						interaction={interaction}
						response={currentValue}
						onAnswerSelect={(value) => {
							if (onAnswerSelect) {
								onAnswerSelect(interaction.responseId, value)
							}
						}}
						disabled={disabled}
						hasSubmitted={showFeedback}
						isCorrect={feedback?.isCorrect}
					/>
					<FeedbackMessage
						responseId={interaction.responseId}
						showFeedback={showFeedback}
						perResponseFeedback={perResponseFeedback}
					/>
				</>
			)
		}

		// Handle choice interactions
		if (interaction.type === "choice") {
			const response = responses?.[interaction.responseId]

			// Derive single-select correctness from selectedChoicesByResponse
			let selected: string[] = []
			if (Array.isArray(response)) {
				selected = response
			} else if (typeof response === "string") {
				selected = [response]
			}
			const perSelectionEntries = selectedChoicesByResponse?.[interaction.responseId]
			const perSelectionTable = perSelectionEntries
				? new Map(perSelectionEntries.map((e) => [e.id, e.isCorrect]))
				: undefined
			let singleIsCorrect: boolean | undefined
			if (interaction.cardinality === "single" && selected.length === 1 && perSelectionTable) {
				const firstCandidate = selected[0]
				if (typeof firstCandidate === "string") {
					singleIsCorrect = perSelectionTable.get(firstCandidate) === true
				}
			}

			const toChoiceInteraction = (d: DisplayChoiceInteraction): ChoiceInteraction => {
				return {
					type: "choiceInteraction",
					responseIdentifier: d.responseId,
					shuffle: false,
					minChoices: d.minChoices,
					maxChoices: d.maxChoices,
					promptHtml: d.promptHtml,
					choices: d.choices.map((c) => ({
						identifier: c.id,
						contentHtml: c.contentHtml,
						inlineFeedbackHtml: c.inlineFeedbackHtml
					}))
				}
			}

			return (
				<>
					<ChoiceInteractionRenderer
						interaction={toChoiceInteraction(interaction)}
						response={response}
						onAnswerSelect={(value) => {
							if (onAnswerSelect) {
								onAnswerSelect(interaction.responseId, value)
							}
						}}
						disabled={disabled}
						hasSubmitted={showFeedback}
						isCorrect={singleIsCorrect}
						selectedChoicesCorrectness={(() => {
							const entries = selectedChoicesByResponse?.[interaction.responseId]
							if (!entries) return undefined
							const table = new Map(entries.map((e) => [e.id, e.isCorrect]))
							return selected.map((id) => ({ id, isCorrect: table.get(id) === true }))
						})()}
					/>
					<FeedbackMessage
						responseId={interaction.responseId}
						showFeedback={showFeedback}
						perResponseFeedback={perResponseFeedback}
					/>
				</>
			)
		}

		// Handle order interactions (drag-and-drop sorting)
		if (interaction.type === "order") {
			return (
				<OrderInteractionBlock
					interaction={interaction}
					responses={responses}
					onAnswerSelect={onAnswerSelect}
					disabled={disabled}
					showFeedback={showFeedback}
					perResponseFeedback={perResponseFeedback}
				/>
			)
		}

		// Handle gap match interactions (fill-in-the-blank with drag/click)
		if (interaction.type === "gapMatch") {
			return (
				<GapMatchInteractionBlock
					interaction={interaction}
					responses={responses}
					onAnswerSelect={onAnswerSelect}
					disabled={disabled}
					showFeedback={showFeedback}
					perResponseFeedback={perResponseFeedback}
					selectedChoicesByResponse={selectedChoicesByResponse}
				/>
			)
		}

		// Handle match interactions (matrix association)
		if (interaction.type === "match") {
			return (
				<MatchInteractionBlock
					interaction={interaction}
					responses={responses}
					onAnswerSelect={onAnswerSelect}
					disabled={disabled}
					showFeedback={showFeedback}
					perResponseFeedback={perResponseFeedback}
					selectedChoicesByResponse={selectedChoicesByResponse}
				/>
			)
		}
	}

	// Unknown block type
	return null
}

// ============================================================================
// Main QTI Renderer
// ============================================================================

/**
 * A controlled, display-only component for rendering QTI assessment items.
 *
 * **This component does NOT include buttons or submission logic.**
 * The consuming app is responsible for:
 * - Managing `responses` state
 * - Rendering Check/Submit/Try Again buttons
 * - Calling validation APIs
 * - Providing feedback data after validation
 *
 * @example
 * ```tsx
 * const [responses, setResponses] = useState({})
 * const [feedback, setFeedback] = useState(null)
 *
 * <QTIRenderer
 *   item={displayItem}
 *   responses={responses}
 *   onResponseChange={(id, value) => setResponses(prev => ({ ...prev, [id]: value }))}
 *   theme="neobrutalist"
 *   showFeedback={!!feedback}
 *   responseFeedback={feedback?.perResponse}
 *   overallFeedback={feedback?.overall}
 *   disabled={!!feedback}
 * />
 *
 * <button onClick={handleCheck}>Check Answer</button>
 * ```
 */
export function QTIRenderer({
	item,
	responses,
	onResponseChange,
	theme = "duolingo",
	showFeedback = false,
	disabled = false,
	choiceCorrectness,
	responseFeedback,
	overallFeedback,
	className
}: QTIRendererProps) {
	// Determine theme attribute value (empty string for default/duolingo)
	const themeAttr = theme === "duolingo" ? undefined : theme

	return (
		<div className={cn("qti-container", className)} data-qti-theme={themeAttr}>
			<div className="space-y-6">
				{item.contentBlocks.map((block, index) => (
					<ContentBlockRenderer
						key={index}
						block={block}
						responses={responses}
						onAnswerSelect={onResponseChange}
						showFeedback={showFeedback}
						disabled={disabled}
						selectedChoicesByResponse={choiceCorrectness}
						perResponseFeedback={responseFeedback}
					/>
				))}
			</div>

			{/* Overall feedback shown at the end */}
			{showFeedback && overallFeedback?.messageHtml && (
				<div className="qti-feedback mt-6 p-6" data-correct={overallFeedback.isCorrect}>
					<HTMLContent html={overallFeedback.messageHtml} />
				</div>
			)}
		</div>
	)
}

export default QTIRenderer
